import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import useAutoOpenAdd from "../common/useAutoOpenAdd";
import { vasBand, VAS_BANDS } from "../vasScale";
import { treatmentBand } from "../treatmentStatus";
import { formatShortDate } from "./dateHelpers";
import {
  recentEpisodeSinceWhen as _recentEpisodeSinceWhen,
  recentResolveSinceWhen as _recentResolveSinceWhen,
} from "./episodeDates";
import ChiefComplaintsService from "./ChiefComplaintsService";
import TrendModal from "./TrendModal";
import { CC_V2_ADD, CC_V2_EDIT, CC_V2_EDIT_CAPTURED, CC_V2_REFRESH } from "./events";
import { ChatBubbleIcon, MdChevronDown } from "./ccIcons";
import MetaTooltip from "../medicines/MetaTooltip";
import { ToolbarPlusIcon } from "../labInvestigations/labIcons";
import { Sparkle } from "../controls";

/**
 * Chief complaints — content column, backed by api-cc-v2-service.
 *
 * The section owns the currently-fetched doc (version + active + past) and
 * nothing else: every write lives in the editor rail. "+ Add complaints"
 * fires CC_V2_ADD and a row's status chip fires CC_V2_EDIT with that record;
 * the rail opens the matching form and submits it. The only dialog left here
 * is the read-only trend chart.
 */

// View trend shows from the very first save. Every persisted complaint
// has at least one StatusEntry (the initial add row), and users want
// to review that single row too — matches the Medicine v2 rule
// (hasTrend >= 1).
const hasTrend = (c) => Array.isArray(c.statusHistory) && c.statusHistory.length >= 1;

// "Since when" for both the active and past complaint tables — start of
// the most recent episode segment. See ./episodeDates for the algorithm.
const recentEpisodeSinceWhen = (c) =>
  _recentEpisodeSinceWhen(
    c.statusHistory,
    (c.current && c.current.sinceWhen) || c.addedAt,
  );

// "Resolved on" for the past complaint table — patient-reported date from
// the audit entry that CLOSED the most recent episode, not the wall-clock
// c.resolvedAt.
const recentResolveSinceWhen = (c) =>
  _recentResolveSinceWhen(c.statusHistory, c.resolvedAt);

const StatusChipButton = ({ score, onClick, disabled }) => {
  const band = vasBand(score);
  return (
    <button
      type="button"
      className="cc3-chip"
      style={{ background: band.bg, color: band.fg }}
      onClick={onClick}
      disabled={disabled}
      title={disabled ? "Resolved — reopen by adding again" : "Update status"}
    >
      <span className="cc3-chip-dot" style={{ background: band.dot || "currentColor" }} aria-hidden="true" />
      <span className="cc3-chip-label">{band.label}</span>
      <span className="cc3-chip-chev" aria-hidden="true"><MdChevronDown /></span>
    </button>
  );
};
StatusChipButton.propTypes = {
  score: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// Past-row "Resolved" pill. Distinct from the active-row VAS band chip so
// clinicians can tell at a glance that this row is closed. Same click →
// same Update-status rail form, which now handles the reopen path.
const TreatmentChipButton = ({ ongoing, onClick }) => {
  const band = treatmentBand(ongoing ? "ongoing" : "resolved");
  return (
    <button
      type="button"
      className="cc3-chip is-small"
      style={{ background: band.bg, color: band.fg }}
      onClick={onClick}
      title="Update status"
    >
      <span className="cc3-chip-dot" style={{ background: band.dot }} aria-hidden="true" />
      <span className="cc3-chip-label">{band.label}</span>
      <span className="cc3-chip-chev" aria-hidden="true"><MdChevronDown /></span>
    </button>
  );
};
TreatmentChipButton.propTypes = {
  ongoing: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

// Map the AI's free-text intensity to a VAS numeric so a staged-add row
// can render the same chip family as CC v2 rows. Unknown → mild.
const vasMinForIntensity = (intensity) => {
  const s = String(intensity || "").trim().toLowerCase();
  const match = VAS_BANDS.find((b) => b.key === s || b.label.toLowerCase().startsWith(s))
    || VAS_BANDS.find((b) => b.key === "mild")
    || VAS_BANDS[0];
  return match.min;
};

// Build a synthetic complaint-shaped row from a staged "add" captured
// item so it can slot into the same table + sort + render code as CC v2
// rows. The `_captured` tag drives the peach background + sparkle in
// the row render.
const stagedAddToRow = (item) => {
  const p = (item && item.payload) || {};
  return {
    complaintId: `staged-${item.id}`,
    complaint:   p.complaint || p.value || "(untitled)",
    complaintCode: p.icd_code ? { icdCode: p.icd_code } : null,
    current: {
      sinceWhen:   p.since_date || p.since_when || null,
      description: p.description || "",
      vas:         vasMinForIntensity(p.intensity),
      consultedDoctor: !!p.consulted_doctor,
    },
    statusHistory: [],
    _captured:      item,
    _capturedIsAi:  (item.confidence || 0) > 0,
    _stagedAdd:     true,
  };
};

const ChiefComplaints = ({ patient, capturedItems, autoOpenAdd, onAutoOpenAdd }) => {
  const patientId = patient && (patient.id || patient.patientID);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pastOpen, setPastOpen] = useState(false);
  const [trendTarget, setTrendTarget] = useState(null);

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new ChiefComplaintsService();
    svc.list(
      patientId,
      (res) => {
        setDoc(res || { active: [], past: [], version: 0 });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load chief complaints");
        setLoading(false);
      }
    );
  }, [patientId]);

  useEffect(() => {
    load();
    EventService.addListener(CC_V2_REFRESH, load);
    return () => EventService.removeListener(CC_V2_REFRESH, load);
  }, [load]);

  // The add form lives in the editor rail now (2961:3685); the toolbar
  // button just asks it to open.
  const openAdd = () => EventService.emit(CC_V2_ADD);
  useAutoOpenAdd({ autoOpenAdd, onAutoOpenAdd, loading, error, doc, open: openAdd });

  // Status updates moved into the rail too (the 2961:4126 card, no longer a
  // centred dialog) — the chip hands the record over and the rail prefills.
  //
  // Transcript-mode re-edits of already-staged captured items fire a
  // distinct event so the sidebar's editor rail doesn't accidentally
  // open an "update CC v2" sheet for a row that isn't in CC v2 yet
  // (staged-add) or already has a pending staged update (dedup).
  const openUpdate = (c) => {
    if (c && c._captured) {
      EventService.emit(CC_V2_EDIT_CAPTURED, {
        captured:    c._captured,
        // For staged-add rows the CC v2 record doesn't exist yet. For
        // staged-update rows we hand over the row minus the annotations
        // so a re-edit can fall back to the original CC v2 seeds.
        ccv2Record:  c._stagedAdd ? null : c,
      });
      return;
    }
    EventService.emit(CC_V2_EDIT, { complaint: c });
  };

  const active = (doc && doc.active) || [];
  const past = (doc && doc.past) || [];

  // Transcript-mode overlay. When `capturedItems` is unpassed / empty
  // (standalone sidebar renders), every derived list falls back to the
  // server's `active` / `past` unchanged.
  //
  //   • operation="add"     state=added_in_call/edited_in_call →
  //                          new synthetic row slotted in
  //   • operation="update"  targetEntryId=X → annotate the existing row
  //                          X. If the update sets VAS to 0 the row is
  //                          re-classified into Past (and vice-versa if
  //                          a past row is re-opened).
  //   • confidence > 0      → sparkle icon on the row.
  const inTranscriptMode = !!(capturedItems && capturedItems.length > 0);
  const stagedForSection = (capturedItems || []).filter(
    (it) => it && (it.state === "added_in_call" || it.state === "edited_in_call"),
  );
  const updatesByEntryId = new Map();
  const addRows = [];
  for (const it of stagedForSection) {
    if (it.operation === "update" && it.targetEntryId) {
      updatesByEntryId.set(String(it.targetEntryId), it);
    } else if (it.operation === "add") {
      addRows.push(stagedAddToRow(it));
    }
  }

  // Compute the "effective" state for a row, folding any staged update
  // on top. Standalone rows without _captured return their untouched
  // values, so past/active membership is unchanged.
  //
  // IMPORTANT: we overwrite `current.*` too, not just `_effectiveVas`.
  // The rest of this file's render reads `c.current.vas`, `.description`
  // etc. directly. Without the overwrite the chip / description column
  // on a staged row would keep showing the ORIGINAL CC v2 values — the
  // staged edits would only show up when the reviewer re-opened the
  // right-side card.
  const withEffective = (row) => {
    const update = row._captured;
    if (!update || update.operation !== "update") {
      return {
        ...row,
        _effectiveVas: (row.current && row.current.vas) || 0,
        _effectiveResolvedAt: row.resolvedAt,
      };
    }
    const p = (update.payload) || {};
    const cur = row.current || {};
    let vas = cur.vas || 0;
    if (typeof p.vas_min === "number") vas = p.vas_min;
    else if (p.vas_band) {
      const b = VAS_BANDS.find((bb) => bb.key === p.vas_band);
      if (b) vas = b.min;
    }
    const resolvedAt = vas === 0
      ? (p.since_when || row.resolvedAt || new Date().toISOString())
      : row.resolvedAt;
    return {
      ...row,
      current: {
        ...cur,
        vas,
        sinceWhen:       p.since_when      || cur.sinceWhen,
        description:     p.description !== undefined
                            ? p.description
                            : cur.description,
        consultedDoctor: typeof p.consulted_doctor === "boolean"
                            ? p.consulted_doctor
                            : cur.consultedDoctor,
      },
      _effectiveVas: vas,
      _effectiveResolvedAt: resolvedAt,
    };
  };

  // Annotate active + past with any staged update targeting them.
  const annotateWithUpdate = (row) => {
    const update = updatesByEntryId.get(String(row.complaintId));
    if (!update) return row;
    return { ...row, _captured: update, _capturedIsAi: (update.confidence || 0) > 0 };
  };

  // Rebuild the two visible lists based on effective VAS. Standalone
  // (no captured items) collapses to the server's original split.
  let activeMerged;
  let pastMerged;
  if (inTranscriptMode) {
    const annotatedActive = active.map(annotateWithUpdate).map(withEffective);
    const annotatedPast   = past.map(annotateWithUpdate).map(withEffective);
    const stagedAddRows   = addRows.map(withEffective);
    const merged = [...annotatedActive, ...annotatedPast, ...stagedAddRows];
    activeMerged = merged
      .filter((r) => (r._effectiveVas || 0) > 0)
      .sort((a, b) => {
        const ad = new Date((a.current && a.current.sinceWhen) || 0).getTime();
        const bd = new Date((b.current && b.current.sinceWhen) || 0).getTime();
        return bd - ad;
      });
    pastMerged = merged
      .filter((r) => (r._effectiveVas || 0) === 0)
      .sort((a, b) => {
        const ad = new Date(a._effectiveResolvedAt || 0).getTime();
        const bd = new Date(b._effectiveResolvedAt || 0).getTime();
        return bd - ad;
      });
  } else {
    activeMerged = active;
    pastMerged   = past;
  }

  if (!patientId) {
    return <div className="ph-slot-empty"><p>Waiting for patient…</p></div>;
  }
  if (loading && !doc) {
    return <div className="ph-slot-empty"><p>Loading chief complaints…</p></div>;
  }
  if (error) {
    return (
      <div className="ph-slot-empty">
        <p>{error}</p>
        <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>Retry</button>
      </div>
    );
  }

  // Two empty shapes:
  //   1. no active AND no past → fresh patient, "No chief complaints recorded"
  //   2. no active BUT past exists → everything has resolved, "No active complaints"
  // In both cases the header `+ Add` button is hidden — the reviewer's next
  // action lives on the empty-state CTA below (matches Figma). The header
  // button reappears the moment there's at least one active row to sit
  // alongside.
  const hasActive = activeMerged.length > 0;
  const hasPast = pastMerged.length > 0;
  const showHeaderAdd = hasActive;
  const emptyCopy = hasPast
    ? {
        title: "No active complaints",
        hint: "All complaints are resolved — they've moved to Past complaints below. Add a complaint when the patient reports a new one.",
        countLabel: "No active",
      }
    : {
        title: "No chief complaints recorded",
        hint: "Nothing has been logged yet. Add the patient's first complaint to begin building their history.",
        countLabel: "No entries",
      };

  return (
    <div className="cc3-wrap">
      <div className="cc3-card">
        <header className="cc3-toolbar">
          <h2 className="cc3-title">Chief complaints</h2>
          <span className="cc3-count">
            &middot;{" "}
            {hasActive
              ? `${activeMerged.length} ${activeMerged.length === 1 ? "entry" : "entries"}`
              : emptyCopy.countLabel}
          </span>
          {showHeaderAdd && (
            <button type="button" className="cc3-add" onClick={openAdd}>
              <ToolbarPlusIcon /> Add complaints
            </button>
          )}
        </header>

        {!hasActive ? (
          <div className="cc-empty">
            <span className="cc-empty-medallion" aria-hidden="true"><ChatBubbleIcon /></span>
            <p className="cc-empty-title">{emptyCopy.title}</p>
            <p className="cc-empty-hint">{emptyCopy.hint}</p>
            <button type="button" className="cc-empty-cta" onClick={openAdd}>
              <ToolbarPlusIcon /> Add complaint
            </button>
          </div>
        ) : (
          <div className="cc3-table" role="table">
            <div className="cc3-thead" role="row">
              <span role="columnheader">Complaint</span>
              <span role="columnheader">Since when</span>
              <span role="columnheader">Description</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Analysis</span>
            </div>

            {activeMerged.map((c) => (
              <div
                className={`cc3-row${c._captured ? " is-captured" : ""}`}
                role="row"
                key={c.complaintId}
              >
                <span className="cc3-cell cc3-c-name" role="cell">
                  {c._capturedIsAi ? (
                    <span className="cc-spark" aria-label="AI captured" title="AI captured">
                      <Sparkle />
                    </span>
                  ) : null}
                  {/* Complaint name is a button — same handler as the
                      Status chip (openUpdate). Mirrors Hospitalisation's
                      "Cause of hospitalisation" column. Text + ICD share
                      one flex item so a wrapping name pulls the ICD
                      along on the same inline flow. */}
                  <button
                    type="button"
                    className="cc3-name-btn cc3-name-txt"
                    onClick={() => openUpdate(c)}
                    title="Update complaint"
                  >
                    {c.complaint}
                    {c.complaintCode && c.complaintCode.icdCode && (
                      <span className="cc-icd-inline"> · ICD {c.complaintCode.icdCode}</span>
                    )}
                  </button>
                </span>
                <span className="cc3-cell cc3-c-since" role="cell">
                  {formatShortDate(recentEpisodeSinceWhen(c))}
                </span>
                <span className="cc3-cell cc3-c-desc" role="cell">
                  {/* 3-line clamp with a white hover tooltip that only
                      reveals when the text is actually being truncated —
                      see MetaTooltip. Native `title` dropped so the
                      tooltip appearance matches the fleet. */}
                  <MetaTooltip
                    text={(c.current && c.current.description) || ""}
                    lines={3}
                  />
                </span>
                <span className="cc3-cell cc3-c-status" role="cell">
                  <StatusChipButton
                    score={(c.current && c.current.vas) || 0}
                    onClick={() => openUpdate(c)}
                  />
                </span>
                <span className="cc3-cell" role="cell">
                  {hasTrend(c) ? (
                    <button
                      type="button"
                      className="cc3-trend"
                      onClick={() => setTrendTarget(c)}
                    >
                      View trend
                    </button>
                  ) : (
                    <span className="cc-dash">—</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past complaints: a dashed band while collapsed (2961:3678); expanded
          it becomes its own card whose toolbar is the toggle (2961:4506). */}
      {!pastOpen ? (
        <div className="cc3-past">
          <button
            type="button"
            className="cc3-past-toggle"
            aria-expanded={false}
            onClick={() => setPastOpen(true)}
          >
            <span className="cc3-past-title">Past complaints</span>
            <span className="cc3-past-count">
              {pastMerged.length} resolved {pastMerged.length === 1 ? "entry" : "entries"}
            </span>
            <span className="cc3-past-chev" aria-hidden="true">
              <MdChevronDown />
            </span>
          </button>
        </div>
      ) : (
        <div className="cc3-card">
          <button
            type="button"
            className="cc3-past-cardbar"
            aria-expanded
            onClick={() => setPastOpen(false)}
          >
            <span className="cc3-past-title">Past complaints</span>
            <span className="cc3-past-count">
              {pastMerged.length} resolved {pastMerged.length === 1 ? "entry" : "entries"}
            </span>
            <span className="cc3-past-chev is-open" aria-hidden="true">
              <MdChevronDown />
            </span>
          </button>

          {pastMerged.length > 0 && (
            <div className="cc3-table is-past" role="table">
              <div className="cc3-thead" role="row">
                <span role="columnheader">Complaint</span>
                <span role="columnheader">Since when</span>
                <span role="columnheader">Description</span>
                <span role="columnheader">Resolved on</span>
                <span role="columnheader">Status</span>
                <span role="columnheader">Analysis</span>
              </div>

              {pastMerged.map((c) => (
                <div
                  className={`cc3-row${c._captured ? " is-captured" : ""}`}
                  role="row"
                  key={c.complaintId}
                >
                  <span className="cc3-cell cc3-c-name" role="cell">
                    {c._capturedIsAi ? (
                      <span className="cc-spark" aria-label="AI captured" title="AI captured">
                        <Sparkle />
                      </span>
                    ) : null}
                    {/* Complaint name is a button — same handler as the
                        Status chip. See active-table sibling above. */}
                    <button
                      type="button"
                      className="cc3-name-btn cc3-name-txt"
                      onClick={() => openUpdate(c)}
                      title="Update complaint"
                    >
                      {c.complaint}
                      {c.complaintCode && c.complaintCode.icdCode && (
                        <span className="cc-icd-inline"> · ICD {c.complaintCode.icdCode}</span>
                      )}
                    </button>
                  </span>
                  <span className="cc3-cell cc3-c-since" role="cell">
                    {formatShortDate(recentEpisodeSinceWhen(c))}
                  </span>
                  <span className="cc3-cell cc3-c-desc" role="cell">
                    {/* 3-line clamp + white hover tooltip; only reveals
                        when the text is actually being truncated. */}
                    <MetaTooltip
                      text={(c.current && c.current.description) || ""}
                      lines={3}
                    />
                  </span>
                  <span className="cc3-cell cc3-c-since" role="cell">
                    {formatShortDate(
                      // Staged AI-transcript resolve wins (its `since_when` is
                      // the patient-reported date on the pending update);
                      // otherwise pull the sinceWhen off the audit's most
                      // recent resolve entry instead of wall-clock resolvedAt.
                      (c._captured && c._effectiveVas === 0 && c._effectiveResolvedAt)
                      || recentResolveSinceWhen(c)
                    )}
                  </span>
                  <span className="cc3-cell cc3-c-status" role="cell">
                    <TreatmentChipButton
                      ongoing={!!c.ongoing}
                      onClick={() => openUpdate(c)}
                    />
                  </span>
                  <span className="cc3-cell" role="cell">
                    {hasTrend(c) ? (
                      <button
                        type="button"
                        className="cc3-trend"
                        onClick={() => setTrendTarget(c)}
                      >
                        View trend
                      </button>
                    ) : (
                      <span className="cc-dash">—</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {trendTarget && (
        <TrendModal
          patientId={patientId}
          complaint={trendTarget}
          onClose={() => setTrendTarget(null)}
        />
      )}
    </div>
  );
};

ChiefComplaints.propTypes = {
  /** Open the add form once the list loads — Create Rx's "+ Add …" (useAutoOpenAdd). */
  autoOpenAdd: PropTypes.bool,
  onAutoOpenAdd: PropTypes.func,
  patient: PropTypes.object,
  /**
   * Optional overlay of captured items from the AI-transcript modal's
   * scratch buffer. Unset / empty → the sidebar behavior (untouched).
   * When passed:
   *   • operation="add"     → new synthetic rows slot into Chief
   *                            complaints, sorted by since-when desc
   *   • operation="update"  → the existing row keyed by targetEntryId
   *                            picks up a peach background
   *   • confidence > 0 on the item marks the row as AI-touched (sparkle)
   */
  capturedItems: PropTypes.array,
};
ChiefComplaints.defaultProps = { capturedItems: [] };

export default ChiefComplaints;
